import React from 'react';
import TextInput from '@cx-web/components/primitives/TextInput';

import { isValidEmailFormat } from './email';
import LoadingSpinner from './LoadingSpinner';
import styles from './MyOCLogin2018.module.css';
import { type ResetPasswordViewProps } from './types';

/**
 * Forgotten-password form ported from the legacy `reset-password-view`. Collects an email and asks the
 * container to call `forgotPassword`; once `submitted`, shows the neutral "if your email is registered…"
 * confirmation and a return-to-login action (the legacy widget never reveals whether the email existed).
 */
const ResetPasswordView: React.FC<ResetPasswordViewProps> = ({
  labelActions,
  loading = false,
  onReturnToLogin,
  onSubmit,
  submitted = false,
}) => {
  const [email, setEmail] = React.useState('');
  const [isValidEmail, setIsValidEmail] = React.useState(true);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    if (!email) return;
    onSubmit(email);
  };

  const buttonText = submitted
    ? labelActions.get('AUTH_RETURN_TO_LOGIN')
    : labelActions.get('AUTH_RESET_PASSWORD_ACTION');

  return (
    <form className={styles.view} onSubmit={submitted ? undefined : handleSubmit}>
      <div className={styles.viewContent}>
        <div className={styles.viewTitle}>{labelActions.get('AUTH_RESET_PASSWORD')}</div>

        {loading && <LoadingSpinner />}

        {!loading && submitted && (
          <div className={styles.resetConfirmation}>{labelActions.get('AUTH_RESET_PASSWORD_THANK_YOU')}</div>
        )}

        {!loading && !submitted && (
          <>
            <span className={styles.resetInstructions}>{labelActions.get('AUTH_RESET_PASSWORD_INSTRUCTIONS')}</span>
            <TextInput
              hasError={!isValidEmail}
              labelName={labelActions.get('AUTH_EMAIL_ADDRESS')}
              name="email"
              onBlur={() => setIsValidEmail(isValidEmailFormat(email))}
              onChange={event => setEmail(event.target.value)}
              placeholder={labelActions.get('AUTH_INPUT_EMAIL')}
              type="email"
              value={email}
              wrapperClassName={styles.field}
            />
            {!isValidEmail && (
              <div className={styles.errorMessage}>{labelActions.get('AUTH_EMAIL_ADDRESS_FORMAT')}</div>
            )}
          </>
        )}

        <div className={styles.buttonWrapper}>
          <button
            className={styles.ctaButton}
            disabled={!submitted && (loading || !email)}
            onClick={submitted ? onReturnToLogin : undefined}
            type={submitted ? 'button' : 'submit'}
          >
            {buttonText}
          </button>
        </div>
      </div>
    </form>
  );
};

export default ResetPasswordView;
